import { money, parseMoney, toDecimalString, type Money } from "@stb/core/money"
import { calculateDocumentGst, calculateLineGst, type GstBreakdown } from "@stb/jurisdiction-au"

import type { Locale } from "@/core/i18n/locale"

import { gstCopyZh } from "../../content/zh/gst-calculator-copy"
import { formatAud } from "./format-aud"

/** Pure model of the free /gst-calculator (T915). All GST maths is `@stb/jurisdiction-au`; nothing here knows 1/11. */

export type GstMode = "add" | "remove"
export interface GstState {
  amount: string
  mode: GstMode
}

export const MAX_GST_LINES = 20
export const DEFAULT_GST_STATE: GstState = { amount: "110", mode: "remove" }

/** Chips under the amount that set it in one tap (T1051). */
export const QUICK_GST_AMOUNTS: readonly { value: string; label: string }[] = [
  { value: "50", label: "A$50" },
  { value: "100", label: "A$100" },
  { value: "250", label: "A$250" },
  { value: "1000", label: "A$1,000" },
]

/** Largest amount AMOUNT_PATTERN accepts, in cents. */
const MAX_AMOUNT_CENTS = 99_999_999_999
/** Arrow-key steps in cents: plain A$1, Shift A$10, Alt A$0.10. */
const STEP_CENTS = { normal: 100, large: 1000, small: 10 } as const
export type GstStep = keyof typeof STEP_CENTS

/** Decimal with at most 2 places and at most 9 whole digits (A$999,999,999.99 is plenty). */
const AMOUNT_PATTERN = /^(\d{1,3}(,\d{3})+|\d{1,9})(\.\d{1,2})?$/
const amountsAre = (mode: GstMode) => (mode === "remove" ? "inclusive" : "exclusive")

export type GstSingle = { kind: "empty" } | { kind: "invalid" } | ({ kind: "ok" } & GstBreakdown)

export type GstMulti =
  | { kind: "empty" }
  | { kind: "invalid"; line?: number }
  | { kind: "ok"; lines: GstBreakdown[]; totals: GstBreakdown }

function parseAmount(text: string): Money | null {
  const t = text.trim()
  if (!AMOUNT_PATTERN.test(t)) return null
  return parseMoney(t, "AUD")
}

export function calculateGstSingle(text: string, mode: GstMode): GstSingle {
  if (text.trim() === "") return { kind: "empty" }
  const amount = parseAmount(text)
  if (!amount) return { kind: "invalid" }
  return {
    kind: "ok",
    ...calculateLineGst({ amount, taxCode: "GST", amountsAre: amountsAre(mode) }),
  }
}

/** Blank lines are skipped; per-line rounding, as on an invoice from this app. */
export function calculateGstLines(texts: readonly string[], mode: GstMode): GstMulti {
  if (texts.length > MAX_GST_LINES) return { kind: "invalid" }
  const filled = texts.map((t, index) => ({ t, index })).filter(({ t }) => t.trim() !== "")
  if (filled.length === 0) return { kind: "empty" }
  const amounts: Money[] = []
  for (const { t, index } of filled) {
    const amount = parseAmount(t)
    if (!amount) return { kind: "invalid", line: index }
    amounts.push(amount)
  }
  const result = calculateDocumentGst(
    amounts.map((amount) => ({ amount, taxCode: "GST" as const })),
    { amountsAre: amountsAre(mode), rounding: "per-line" },
  )
  return { kind: "ok", lines: result.lines, totals: result.totals }
}

/** Whitelist parse: anything unknown or malformed falls back to the default, never throws. */
export function parseGstSearch(search: string): GstState {
  const params = new URLSearchParams(search)
  const amount = params.get("amount")
  const mode = params.get("mode")
  return {
    amount: amount !== null && AMOUNT_PATTERN.test(amount) ? amount : DEFAULT_GST_STATE.amount,
    mode: mode === "add" || mode === "remove" ? mode : DEFAULT_GST_STATE.mode,
  }
}

export function toGstSearch({ amount, mode }: GstState): string {
  const params = new URLSearchParams()
  if (AMOUNT_PATTERN.test(amount)) params.set("amount", amount)
  params.set("mode", mode)
  return `?${params.toString()}`
}

/** Field text for an amount: "111" for whole dollars, "109.90" otherwise. */
function toAmountText(value: Money): string {
  const text = toDecimalString(value)
  return text.endsWith(".00") ? text.slice(0, -3) : text
}

/**
 * ArrowUp/ArrowDown on the amount (T1051): one step up or down in whole cents, never below 0 or above the largest
 * accepted amount. An empty field counts as 0; an invalid one is returned unchanged.
 */
export function stepGstAmount(text: string, direction: 1 | -1, step: GstStep = "normal"): string {
  const current = text.trim() === "" ? money(0, "AUD") : parseAmount(text)
  if (!current) return text
  const next = Math.min(
    MAX_AMOUNT_CENTS,
    Math.max(0, current.amount + direction * STEP_CENTS[step]),
  )
  return toAmountText(money(next, "AUD"))
}

/** GST share of the total in basis points, for the split bar width and its label. Display only, not an amount. */
export function gstShareBp(t: GstBreakdown): number {
  return t.gross.amount > 0 ? Math.round((t.gst.amount * 10_000) / t.gross.amount) : 0
}

/** A keyboard shortcut row of the dialog; mirrors `CalcShortcut` in calc-ui without importing a component. */
export interface GstShortcutCopy {
  keys: string[]
  separator?: "+" | "/"
  action: string
}

/** Every visible and spoken word of the calculator, per locale (the zh object lives in content/zh). */
export interface GstCopy {
  modes: Record<GstMode, string>
  shortcuts: GstShortcutCopy[]
  yourAmount: string
  iWantTo: string
  amountIncl: string
  amountExcl: string
  lineLabel: (line: number, including: boolean) => string
  removeLine: (line: number) => string
  addLine: string
  invalid: string
  invalidLine: (line: number) => string
  hintSingle: string
  hintMulti: string
  headline: {
    remove: (gross: string) => string
    add: (net: string) => string
    invalid: string
    empty: string
  }
  rows: { net: string; gst: string; gross: string }
  quickAmounts: string
  several: (max: number) => string
  keysHint: { change: string; ten: string; all: string }
  result: string
  breakdownCaption: string
  roundedPerLine: string
  copyLink: string
  linkCopied: string
  copyFailed: string
  shareSingle: string
  privacy: string
  disclaimer: string
  visual: {
    heading: string
    net: string
    gst: string
    gross: string
    total: string
    showTable: string
    caption: string
    columns: [string, string, string, string]
    remove: [string, string]
    add: [string, string]
  }
}

const GST_COPY_EN: GstCopy = {
  modes: { add: "Add GST", remove: "Remove GST" },
  shortcuts: [
    { keys: ["↑", "↓"], separator: "/", action: "Change the amount by A$1" },
    { keys: ["Shift", "↑ ↓"], action: "Change by A$10" },
    { keys: ["Alt", "↑ ↓"], action: "Change by A$0.10" },
    { keys: ["←", "→"], separator: "/", action: "Switch between Add GST and Remove GST" },
    { keys: ["?"], action: "Show these shortcuts" },
    { keys: ["Esc"], action: "Close this list" },
  ],
  yourAmount: "Your amount",
  iWantTo: "I want to",
  amountIncl: "Amount including GST",
  amountExcl: "Amount excluding GST",
  lineLabel: (line, including) =>
    `Line ${line}: ${including ? "amount including gst" : "amount excluding gst"}`,
  removeLine: (line) => `Remove line ${line}`,
  addLine: "Add line",
  invalid: "Use an amount in dollars and cents, such as 110 or 99.95.",
  invalidLine: (line) => `Line ${line}: use an amount in dollars and cents, such as 110 or 99.95.`,
  hintSingle: "Dollars and cents, such as 110 or 99.95.",
  hintMulti: "Blank lines are skipped.",
  headline: {
    remove: (gross) => `GST included in ${gross}`,
    add: (net) => `GST to add to ${net}`,
    invalid: "Check the amount you entered",
    empty: "Enter an amount to see the GST",
  },
  rows: { net: "Price excluding GST", gst: "GST (10%)", gross: "Price including GST" },
  quickAmounts: "Quick amounts",
  several: (max) => `Several items (up to ${max} lines, GST rounded per line, then added)`,
  keysHint: { change: "change by A$1", ten: "×10", all: "all shortcuts" },
  result: "Result",
  breakdownCaption: "GST breakdown",
  roundedPerLine: "GST rounded on each line, then added",
  copyLink: "Copy link to this result",
  linkCopied: "Link copied",
  copyFailed: "Could not copy. Copy the address bar instead.",
  shareSingle: "The share link holds a single amount.",
  privacy: "Runs in your browser. Nothing is sent to us.",
  disclaimer:
    "Estimate only. Not tax advice. Uses the standard 10% GST; GST-free and input-taxed sales have no GST to add.",
  visual: {
    heading: "How the GST adds up",
    net: "Price excluding GST",
    gst: "GST",
    gross: "Price including GST",
    total: "Total including GST",
    showTable: "Show as a table",
    caption: "How the GST adds up",
    columns: ["Part", "Sum", "Amount", "Share"],
    remove: ["GST is one eleventh of the total", "Take it off to get the price excluding GST"],
    add: ["Multiply by 1.1 to get the total", "Take the price off to see the GST"],
  },
}

export const gstCopy = (locale: Locale = "en"): GstCopy =>
  locale === "zh" ? gstCopyZh : GST_COPY_EN

/** The line under the amount: the format hint, or the reason the entry is not an amount (and which line). */
export function gstHint(
  kind: "empty" | "invalid" | "ok",
  multi: boolean,
  badLine: number | undefined,
  locale: Locale = "en",
): string {
  const c = gstCopy(locale)
  if (kind !== "invalid") return multi ? c.hintMulti : c.hintSingle
  return badLine !== undefined ? c.invalidLine(badLine + 1) : c.invalid
}

/** The one-line headline of the result panel. */
export function gstHeadline(
  totals: GstBreakdown | null,
  kind: "empty" | "invalid" | "ok",
  mode: GstMode,
  locale: Locale = "en",
): string {
  const c = gstCopy(locale).headline
  if (totals)
    return mode === "remove" ? c.remove(formatAud(totals.gross)) : c.add(formatAud(totals.net))
  return kind === "invalid" ? c.invalid : c.empty
}

/** "A$110.00 ÷ 11 = A$10.00"; "≈" when the GST was rounded to the cent. Numbers and symbols only, same in every language. */
export function gstWorkings(t: GstBreakdown, mode: GstMode): string {
  if (mode === "remove") {
    const sign = t.gst.amount * 11 === t.gross.amount ? "=" : "≈"
    return `${formatAud(t.gross)} ÷ 11 ${sign} ${formatAud(t.gst)}`
  }
  const sign = t.gst.amount * 10 === t.net.amount ? "=" : "≈"
  return `${formatAud(t.net)} × 0.1 ${sign} ${formatAud(t.gst)}`
}
